import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Search, Image as ImageIcon, Loader2, Edit3, ChevronRight,
  Database, Sparkles
} from 'lucide-react';
import VehicleDetailView from '../../components/admin/VehicleDetailView';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminVehicleEditor = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [brands, setBrands] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  const loadBrands = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/vehicle-master/statistics`, { headers: authHeaders() });
      setBrands(res.data?.brands || []);
    } catch (e) {
      console.error('Brands load fail', e);
    }
  }, []);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery.trim()) params.append('q', searchQuery.trim());
      if (brandFilter) params.append('brand', brandFilter);
      params.append('limit', '50');
      const res = await axios.get(
        `${API}/api/admin/vehicle-master/vehicles?${params}`,
        { headers: authHeaders() }
      );
      setVehicles(res.data?.vehicles || []);
      setTotalCount(res.data?.total || 0);
    } catch (err) {
      toast.error('Suche fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, brandFilter]);

  useEffect(() => {
    loadBrands();
    search();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => search(), 350);
    return () => clearTimeout(t);
  }, [searchQuery, brandFilter]);

  const openVehicle = async (vehicle) => {
    setSelectedVehicleId(vehicle.id);
    try {
      const res = await axios.get(
        `${API}/api/admin/vehicle-master/vehicles/${vehicle.id}`,
        { headers: authHeaders() }
      );
      setSelectedVehicle(res.data);
    } catch (err) {
      toast.error('Fahrzeug konnte nicht geladen werden');
    }
  };

  const handleSaveVehicle = async (updatedVehicle) => {
    try {
      const res = await axios.put(
        `${API}/api/admin/vehicle-master/vehicles/${updatedVehicle.id}`,
        updatedVehicle,
        { headers: authHeaders() }
      );
      setSelectedVehicle(res.data);
      toast.success('Fahrzeug aktualisiert');
      search();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen');
    }
  };

  const enrichVehicle = async () => {
    if (!selectedVehicle) return;
    setEnriching(true);
    try {
      const res = await axios.post(
        `${API}/api/data-pipeline/enrich`,
        {
          brand: selectedVehicle.brand,
          model: selectedVehicle.model,
          force: true,
          vehicle_id: selectedVehicle.id,
        },
        { headers: authHeaders() }
      );
      if (res.data?.success) {
        const s = res.data.save_stats || {};
        const parts = [];
        if (s.models_added) parts.push(`+${s.models_added} Modelle`);
        if (s.sources_saved) parts.push(`+${s.sources_saved} Quellen`);
        if (res.data.vehicle_updated) parts.push('✓ Fahrzeug aktualisiert');
        toast.success(parts.length ? `Anreicherung: ${parts.join(', ')}` : 'Anreicherung: keine neuen Daten');
        await openVehicle(selectedVehicle);
      } else {
        const errMsg = res.data?.budget_blocked ? '⚠️ ' + res.data.error : res.data?.error || 'Anreicherung fehlgeschlagen';
        toast.error(errMsg);
      }
    } catch (err) {
      toast.error('Anreicherung fehlgeschlagen');
    } finally {
      setEnriching(false);
    }
  };

  const generateImage = async () => {
    if (!selectedVehicle) return;
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/vehicle-master/generate-image/${selectedVehicle.id}`,
        {},
        { headers: authHeaders() }
      );
      if (res.data?.success) {
        toast.success('Bild generiert');
        await openVehicle(selectedVehicle);
      } else {
        toast.error(res.data?.error || 'Bildgenerierung fehlgeschlagen');
      }
    } catch (err) {
      toast.error('Bildgenerierung fehlgeschlagen: ' + (err.response?.data?.detail || err.message));
    } finally {
      setGenerating(false);
    }
  };

  const closeDetail = () => {
    setSelectedVehicleId(null);
    setSelectedVehicle(null);
  };

  return (
    <div className="min-h-screen lg:ml-64 p-4 sm:p-6" style={{ backgroundColor: '#0B0F14' }}>
      <div className="max-w-7xl mx-auto space-y-4">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
              <Edit3 className="w-7 h-7 text-cyan-400" />
              Fahrzeug-Editor
            </h1>
            <p className="text-gray-400 text-sm mt-1">
              Schnelle Suche und vollständige Bearbeitung aller Fahrzeugdaten
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Database className="w-4 h-4" />
            {totalCount} Treffer
          </div>
        </div>

        {/* Search */}
        <Card className="bg-[#0F141A] border-white/10">
          <CardContent className="p-4">
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  data-testid="vehicle-editor-search"
                  placeholder="Marke, Modell, Variante, ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-white/5 border-white/10 text-white"
                />
              </div>
              <select
                value={brandFilter}
                onChange={(e) => setBrandFilter(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm"
                data-testid="vehicle-editor-brand-filter"
              >
                <option value="">Alle Marken</option>
                {brands.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Results Grid */}
        <Card className="bg-[#0F141A] border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm flex items-center justify-between">
              Fahrzeuge
              {loading && <Loader2 className="w-4 h-4 animate-spin text-cyan-400" />}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {vehicles.length === 0 && !loading ? (
              <p className="text-gray-500 text-center py-8 text-sm">Keine Fahrzeuge gefunden</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 p-3">
                {vehicles.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => openVehicle(v)}
                    data-testid={`vehicle-row-${v.id}`}
                    className="text-left p-3 rounded-lg hover:bg-white/5 transition-colors flex items-center gap-3 border border-white/5 hover:border-cyan-500/30 bg-white/[0.02]"
                  >
                    {v.image?.path ? (
                      <img
                        src={`${API}/api${v.image.path}`}
                        alt=""
                        className="w-14 h-14 rounded object-cover flex-shrink-0"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-14 h-14 rounded bg-white/5 flex items-center justify-center flex-shrink-0">
                        <ImageIcon className="w-5 h-5 text-gray-600" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-sm font-medium truncate">
                        {v.brand} {v.model} {v.variant || ''}
                      </p>
                      <p className="text-xs text-gray-400 truncate">
                        {v.year_start || '?'} – {v.year_end || 'heute'} • {v.fuel_type || '—'}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-cyan-400"
                            style={{ width: `${v.completeness || 0}%` }}
                          />
                        </div>
                        <span className="text-[10px] text-gray-500">{v.completeness || 0}%</span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail Modal - using shared VehicleDetailView component */}
      {selectedVehicleId && selectedVehicle && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto"
          onClick={(e) => e.target === e.currentTarget && closeDetail()}
        >
          <div className="min-h-screen p-4 flex items-start justify-center">
            <div className="w-full max-w-7xl bg-[#0F141A] rounded-2xl border border-white/10 my-4">
              {/* Quick action toolbar */}
              <div className="flex flex-wrap items-center justify-between gap-2 p-4 border-b border-white/10 sticky top-0 bg-[#0F141A] z-10 rounded-t-2xl">
                <div className="flex items-center gap-2">
                  <Edit3 className="w-4 h-4 text-cyan-400" />
                  <span className="text-white text-sm font-medium">
                    {selectedVehicle.brand} {selectedVehicle.model}
                  </span>
                  <span className="text-xs text-gray-500 font-mono ml-2">{selectedVehicle.id}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={enrichVehicle}
                    disabled={enriching}
                    className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:opacity-90 h-8"
                    data-testid="enrich-vehicle-btn"
                  >
                    {enriching ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                    Daten anreichern
                  </Button>
                  <Button
                    size="sm"
                    onClick={generateImage}
                    disabled={generating}
                    className="bg-amber-600 hover:bg-amber-700 h-8"
                    data-testid="generate-image-btn"
                  >
                    {generating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ImageIcon className="w-3 h-3 mr-1" />}
                    Bild generieren
                  </Button>
                </div>
              </div>

              {/* The shared user-style detail view */}
              <VehicleDetailView
                vehicle={selectedVehicle}
                onClose={closeDetail}
                onSave={handleSaveVehicle}
                isAdmin={true}
                showHeader={true}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVehicleEditor;
